/**
 * This is a seperate file to help with code splitting
 */
import React from "react";

import type { CellContentsMP } from "../../redux/slices/hexVersionMPSlice.js";
import { ClientCounterEvent } from "../../util/clientCounterEvent.js";
import {
  ChartCellIcon,
  CodeCellIcon,
  ComponentIcon,
  DropdownInputIcon,
  FilterCellIcon,
  SQLCellIcon,
  SectionIcon,
  TextCellIcon,
  UnorderedListIcon,
  WritebackCellIcon,
} from "../icons/CustomIcons.js";

type CellFilterShowTypeKey =
  | "All"
  | "Code"
  | "Components"
  | "Input"
  | "Sections"
  | "Sql"
  | "Text"
  | "Transformations"
  | "Visualizations"
  | "Writeback";

export const CellFilterShowType: Record<
  CellFilterShowTypeKey,
  {
    display: string;
    label: string;
    typename?: string | readonly string[];
    icon?: JSX.Element;
    counterEvent: ClientCounterEvent;
  }
> = {
  All: {
    display: "All",
    label: "all",
    icon: <UnorderedListIcon />,
    counterEvent: "sidebar.outline.filter.all",
  },
  Code: {
    // Label as python to match what we have in the "New cell" bar.
    display: "Python",
    label: "Python",
    typename: "CodeCell",
    icon: <CodeCellIcon />,
    counterEvent: "sidebar.outline.filter.code",
  },
  Components: {
    display: "Components",
    label: "Components",
    typename: "ComponentImportCell",
    icon: <ComponentIcon />,
    counterEvent: "sidebar.outline.filter.components",
  },
  Input: {
    display: "Inputs",
    label: "Inputs",
    typename: "Parameter",
    icon: <DropdownInputIcon />,
    counterEvent: "sidebar.outline.filter.inputs",
  },
  Sections: {
    display: "Sections",
    label: "Sections",
    typename: "CollapsibleCell",
    icon: <SectionIcon />,
    counterEvent: "sidebar.outline.filter.sections",
  },
  Sql: {
    display: "SQL",
    label: "SQL",
    typename: "SqlCell",
    icon: <SQLCellIcon />,
    counterEvent: "sidebar.outline.filter.sql",
  },
  Text: {
    display: "Text",
    label: "Text",
    typename: ["MarkdownCell", "TextCell"],
    icon: <TextCellIcon />,
    counterEvent: "sidebar.outline.filter.text",
  },
  Transformations: {
    display: "Transformations",
    label: "Transformations",
    typename: ["FilterCell", "PivotCell"],
    icon: <FilterCellIcon />,
    counterEvent: "sidebar.outline.filter.transformations",
  },
  Visualizations: {
    display: "Visualizations",
    label: "Visualizations",
    typename: ["ChartCell", "DisplayTableCell", "MetricCell", "MapCell"],
    icon: <ChartCellIcon />,
    counterEvent: "sidebar.outline.filter.visualizations",
  },
  Writeback: {
    display: "Writeback",
    label: "Writeback",
    typename: ["WritebackCell"],
    icon: <WritebackCellIcon />,
    counterEvent: "sidebar.outline.filter.writeback",
  },
};

/**
 * Helper function to determine if an ExploreCell should be included in a specific filter category
 * based on its visualizationType.
 */
export function shouldIncludeExploreCellInFilter(
  cellContents: CellContentsMP,
  filterKey: CellFilterShowTypeKey,
): boolean {
  if (cellContents.__typename !== "ExploreCell") {
    return false;
  }

  const { visualizationType } = cellContents.spec;

  switch (filterKey) {
    case "Transformations":
      return visualizationType === "pivot-table";
    case "Visualizations":
      return true;
    default:
      return false;
  }
}
